import { useEffect, useMemo, useState } from 'react';

import type { Appointment } from '@/react-query/useAppointments';
import type { ClaimWritebackStatus } from '@/react-query/useWritebackStatus';

import type { TTreatmentResponse } from '../../freeClaims/insuranceExport/types';
import { getClaimStatusForAppointment, getDisplayName } from '../helpers';

type SortField =
  | 'patient'
  | 'appointment'
  | 'provider'
  | 'primaryPayer'
  | 'value'
  | 'status';
type SortDirection = 'asc' | 'desc';

export const useClaimsSorting = (
  appointments: (Appointment & {
    treatments: TTreatmentResponse[];
    allTreatmentsExcluded: boolean;
    payerName?: string;
    writebackStatus?: ClaimWritebackStatus;
  })[],
  isSearchActive: boolean = false,
) => {
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Sort appointments
  const sortedAppointments = useMemo(() => {
    const sorted = [...appointments].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'patient': {
          const nameA = getDisplayName(a, 0).patientName.toLowerCase();
          const nameB = getDisplayName(b, 0).patientName.toLowerCase();
          return nameA.localeCompare(nameB) * direction;
        }
        case 'appointment': {
          const dateA = new Date(a.appointment.start).getTime();
          const dateB = new Date(b.appointment.start).getTime();
          return (dateA - dateB) * direction;
        }
        case 'provider': {
          const providerA = `${a.appointment.provider?.firstName || ''} ${
            a.appointment.provider?.lastName || ''
          }`.toLowerCase();
          const providerB = `${b.appointment.provider?.firstName || ''} ${
            b.appointment.provider?.lastName || ''
          }`.toLowerCase();
          return providerA.localeCompare(providerB) * direction;
        }
        case 'primaryPayer': {
          const payerA = (a.payerName || '').toLowerCase();
          const payerB = (b.payerName || '').toLowerCase();
          return payerA.localeCompare(payerB) * direction;
        }
        case 'value': {
          // Placeholder value logic - using the same logic as in the component
          const getStableValue = (appointment: Appointment) => {
            const str = appointment.appointment.unifiedId || '';
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
              hash += str.charCodeAt(i);
            }
            return (hash % 900) + 100;
          };

          const valueA = getStableValue(a);
          const valueB = getStableValue(b);
          return (valueA - valueB) * direction;
        }
        case 'status': {
          const STATUS_ORDER: Record<string, number> = {
            ready: 0,
            review: 1,
            submitted: 2,
            'no-attachment': 3,
          };
          const claimStatusA = getClaimStatusForAppointment(a);
          const claimStatusB = getClaimStatusForAppointment(b);
          const orderDiff =
            (STATUS_ORDER[claimStatusA] ?? 99) -
            (STATUS_ORDER[claimStatusB] ?? 99);
          if (orderDiff !== 0) return orderDiff * direction;

          // Same status — secondary sort by appointment date
          const dateA = new Date(a.appointment.start).getTime();
          const dateB = new Date(b.appointment.start).getTime();
          return (dateA - dateB) * direction;
        }
        default:
          return 0;
      }
    });

    return sorted;
  }, [appointments, sortField, sortDirection]);

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  // When searching, default to descending order for appointment dates
  useEffect(() => {
    if (
      isSearchActive &&
      sortField === 'appointment' &&
      sortDirection === 'asc'
    ) {
      setSortDirection('desc');
    }
  }, [isSearchActive, sortField, sortDirection]);

  return {
    sortedAppointments,
    sortField,
    sortDirection,
    handleSort,
  };
};
